import { RadioGroup } from '@headlessui/react'
import React, { MouseEvent, ReactElement, ReactNode, createContext, useCallback, useContext } from 'react'

import clsx from 'clsx'

interface RadioOptionProps {
  value: string
  label?: string
  checked?: boolean
  disabled?: boolean
  children?: ({
    active,
    checked,
    onClick
  }: {
    checked: boolean
    active: boolean
    onClick?: (e: MouseEvent) => void
  }) => ReactElement
  className?: string
  containerClassName?: string
}

export type RadioLayout = 'vertical' | 'horizontal'

export interface RadioProps {
  children: ReactNode
  value?: string
  onChange?: (value: string) => void
}

type RadioContextType = {
  layout: RadioLayout
  selectedValue: string
  onChange?: (value: string) => void
}

type RadioProviderProps = {
  children: React.ReactNode
} & RadioContextType

type RadioCheckboxProps = {
  active?: boolean
  checked?: boolean
  className?: string
  disabled?: boolean
}

export const RadioContext = createContext<RadioContextType>({
  layout: 'vertical',
  onChange: undefined,
  selectedValue: ''
})

export const RadioProvider = ({ children, layout, onChange, selectedValue }: RadioProviderProps) => {
  return <RadioContext.Provider value={{ layout, onChange, selectedValue }}>{children}</RadioContext.Provider>
}

export const RadioCheckbox = ({ checked, className, disabled }: RadioCheckboxProps) => (
  <div
    className={clsx(
      'border-border-default h-5 w-5 shrink-0 rounded-full border border-2 text-white',
      !disabled && 'hover:border-background-default hover:bg-primary-default hover:ring-primary-default hover:ring-2',
      !disabled && checked && '!border-background-default bg-primary-default ring-primary-default border-2 ring-2',
      disabled && 'bg-background-default',
      className
    )}
  />
)

export const RadioOption: React.FC<RadioOptionProps> = ({
  checked,
  children,
  className,
  containerClassName,
  disabled,
  label,
  value
}) => {
  const { layout, onChange, selectedValue } = useContext(RadioContext)

  const handleChange = useCallback(
    e => {
      const value = e.target.value
      onChange?.(value)
    },
    [onChange, selectedValue]
  )

  const active = !!onChange

  if (children && typeof children !== 'function') {
    throw new Error('Invalid prop: children must be a function if it is provided.')
  }

  const optionClasses = clsx(
    'cursor-pointer mb-2',
    {
      'flex items-center': typeof children !== 'function',
      'mr-2': layout === 'horizontal'
    },
    className
  )

  return (
    <RadioGroup.Option className={containerClassName} value={value}>
      <label className={optionClasses}>
        {!children ? <RadioCheckbox checked={checked} className='mr-2' disabled={disabled} /> : null}
        {label ? <span className='mr-2'>{label}</span> : null}
        {typeof children === 'function'
          ? children({ active, checked: selectedValue === value, onClick: handleChange })
          : null}
      </label>
    </RadioGroup.Option>
  )
}

export const Radio: React.FC<RadioProps> = ({ children }) => {
  const { layout, onChange, selectedValue } = useContext(RadioContext)
  const containerClasses = clsx('flex checkbox_container', {
    'flex-col': layout === 'vertical',
    'flex-row': layout === 'horizontal',
    'flex-wrap': true
  })

  return (
    <RadioGroup onChange={onChange}>
      <div className={containerClasses}>
        {React.Children.map(children, child => {
          if (React.isValidElement<RadioOptionProps>(child)) {
            return React.cloneElement<RadioOptionProps>(child, {
              checked: child.props.value === selectedValue,
              className: clsx(child.props.className)
            })
          }
          return child
        })}
      </div>
    </RadioGroup>
  )
}
